import { Location } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { faArrowLeft, faBoxes, faEllipsisVertical, faLayerGroup, faMap, faPen, faSeedling, faSlidersH, faTractor, faTruck } from '@fortawesome/free-solid-svg-icons';
import { forkJoin, Observable } from 'rxjs';
import { AuthService } from '../../../../../@core/auth/auth.service';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { InventoryService } from '../../../pick-plan/services/inventory.service';
import { HandlingUnitMasterDTO } from '../../models/handling-unit-master';
import { InventoryLotDTO } from '../../models/inventory-lot';
import { LotActionsService } from '../../services/lot-actions.service';
import { MassBalanceComponent } from '../../widgets/mass-balance/mass-balance.component';
import { groupUnitRows, LotUnitValue, summarizeUnits } from './lot-unit-summary';

@Component({
  selector: 'lot-dashboard',
  templateUrl: './lot-dashboard.component.html',
  styleUrls: ['./lot-dashboard.component.scss']
})
export class LotDashboardComponent implements OnInit {
  @ViewChild(MassBalanceComponent) massBalance: MassBalanceComponent;

  public faArrowLeft = faArrowLeft;
  public faBoxes = faBoxes;
  public faEllipsisVertical = faEllipsisVertical;
  public faLayerGroup = faLayerGroup;
  public faMap = faMap;
  public faPen = faPen;
  public faSeedling = faSeedling;
  public faSlidersH = faSlidersH;
  public faTractor = faTractor;
  public faTruck = faTruck;

  public lotId: number;
  public lot: InventoryLotDTO;
  public isLoading = true;
  public isAdmin = false;
  public canViewOrders = false;

  public activeHandlingUnits: HandlingUnitMasterDTO[] = [];
  public productNames = '';
  public varieties = '';
  public unitTotals: LotUnitValue[] = [];
  public barcodeCount = 0;
  public totalWeight = 0;

  private destroyRef = inject(DestroyRef);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private inventoryService: InventoryService,
    private lotActions: LotActionsService,
    public authService: AuthService,
    private errorService: ErrorService
  ) { }

  ngOnInit() {
    this.lotId = +this.route.snapshot.paramMap.get('id') || 0;
    this.isAdmin = this.authService.hasRole('inventory:admin');
    this.canViewOrders = this.authService.hasRole('web-store:admin');
    this.load();
  }

  load() {
    this.isLoading = true;
    forkJoin([
      this.inventoryService.getLots(),
      this.inventoryService.getInventoryHUMs(true, this.lotId)
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([lots, hums]) => {
        this.lot = (lots ?? []).find(lot => lot.id == this.lotId);

        const lotHums = (hums ?? []).filter(hum => hum.handlingUnitDetails?.some(hud => hud.lotId == this.lotId));
        this.activeHandlingUnits = lotHums.filter(hum => hum.isActive);
        this.setSummary(lotHums);
        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  back() {
    if (window.history.length > 1)
      this.location.back();
    else
      this.router.navigateByUrl('/harvest-engine/inventory/lots-list');
  }

  editLot() {
    this.runAction(this.lotActions.editLot(this.lot));
  }

  editProduct() {
    this.runAction(this.lotActions.editProduct(this.lot));
  }

  moveInventory() {
    this.runAction(this.lotActions.moveInventory(this.lot));
  }

  adjustQuantity() {
    this.runAction(this.lotActions.adjustQuantity(this.lot));
  }

  private runAction(action: Observable<boolean>) {
    if (!this.lot) return;

    action.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(changed => {
      if (!changed) return;
      this.load();
      this.massBalance?.reload();
    });
  }

  private setSummary(lotHums: HandlingUnitMasterDTO[]) {
    const lotHuds = lotHums.flatMap(hum => hum.handlingUnitDetails ?? []).filter(hud => hud.lotId == this.lotId);
    const activeHuds = this.activeHandlingUnits.flatMap(hum => hum.handlingUnitDetails ?? []).filter(hud => hud.lotId == this.lotId);
    const summary = summarizeUnits(groupUnitRows(activeHuds, hud => hud.itemName ?? '', hud => [hud]));

    this.unitTotals = summary.totals;
    this.totalWeight = summary.totalWeight;
    this.barcodeCount = new Set(this.activeHandlingUnits.map(hum => hum.barcode)).size;
    this.productNames = this.uniqueJoined(activeHuds.map(hud => hud.itemName));
    this.varieties = this.uniqueJoined(lotHuds.map(hud => hud.varietyName));
  }

  private uniqueJoined(values: string[]): string {
    return [...new Set(values.filter(Boolean))].join(', ');
  }
}
